import React, { useState, useEffect } from "react";
import { Spinner } from "@/components/shared/LoadingSystem";
import "./Section.css";
import ImageUploader from "./ImageUploader";
import fileService from "@shared/api/filesService";

const ImagenesEvidencia = ({
  seccion,
  updateItemResponse,
  renderCase = "view",
  reportInfo,
}) => {
  const [imageItems, setImageItems] = useState([]);
  const [showAddImage, setShowAddImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cargar imágenes existentes y añadidas dinámicamente
  useEffect(() => {
    if (seccion && seccion.items) {
      // Filtrar items con respuestas de tipo imagen
      const existingImages = seccion.items
        .filter((item) => item.idtiporespuesta === 4)
        .map((item) => ({
          ...item,
        }));

      // Verificar si hay imágenes temporales en el estado
      const tempImages = imageItems.filter(
        (item) =>
          item.id.toString().includes("temp-") &&
          !existingImages.some((ei) => ei.id === item.id)
      );

      // Unir imágenes existentes y temporales
      const allImages = [...existingImages, ...tempImages];
      setImageItems(allImages);
    }
  }, [seccion]);

  // Función para añadir una nueva imagen
  const handleAddImage = () => {
    setShowAddImage(true);
  };

  // Función para guardar una nueva imagen
  const handleSaveImage = (imagePath, description) => {
    if (!imagePath) {
      setShowAddImage(false);
      return;
    }

    // Datos de la imagen
    const imageData = {
      valorimagen: imagePath,
      descripcion: description || "Sin descripción",
    };

    // Crear un nuevo item temporal
    const newTempItem = {
      id: `temp-${Date.now()}`,
      descripcion: description || "Imagen de evidencia",
      idtiporespuesta: 4,
      respuesta: imageData,
      isNew: true,
    };

    // Actualizar el estado local con la nueva imagen
    setImageItems((prev) => [...prev, newTempItem]);

    // Notificar al componente padre sobre el nuevo item
    if (updateItemResponse) {
      updateItemResponse(
        seccion.idseccionreporte,
        newTempItem.id,
        null,
        null,
        null,
        null,
        null,
        imageData,
        null,
        description
      );
    }

    setShowAddImage(false);
  };

  // Función para eliminar una imagen
  const handleDeleteImage = (itemId) => {
    setImageItems((prev) => prev.filter((item) => item.id !== itemId));

    if (updateItemResponse) {
      updateItemResponse(
        seccion.idseccionreporte,
        itemId,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null
      );
    }
  };

  // Función para abrir el modal con una imagen específica
  const openImageModal = (item) => {
    setSelectedImage(item);
    setIsModalOpen(true);
  };

  // Función para cerrar el modal
  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <div className="section">
      <h3 className="section-title">{seccion.nombreseccion}</h3>

      {/* Grid de imágenes */}
      {imageItems.length > 0 ? (
        <div className="evidence-grid">
          {imageItems.map((item) => (
            <ImageItem
              key={item.id}
              item={item}
              renderCase={renderCase}
              onDelete={() => handleDeleteImage(item.id)}
              onSelect={() => openImageModal(item)}
              reportInfo={reportInfo}
            />
          ))}
        </div>
      ) : (
        <div className="empty-evidence-message">
          <i className="fa fa-image"></i>
          <p>
            {renderCase === "view"
              ? "No hay imágenes disponibles en este reporte"
              : "Agrega imágenes usando el botón de abajo"}
          </p>
        </div>
      )}

      {/* Botón para añadir imagen (solo en modo creación) */}
      {renderCase === "create" && (
        <div className="add-image-container">
          {showAddImage ? (
            <div className="new-image-uploader">
              <ImageUploader
                onImageUpload={handleSaveImage}
                idReporte={reportInfo?.idreporte || ""}
                showDescription={true}
                buttonLabel="Guardar Imagen"
                cancelButton={true}
                onCancel={() => setShowAddImage(false)}
              />
            </div>
          ) : (
            <button className="add-image-button" onClick={handleAddImage}>
              <i className="fa fa-plus"></i> Agregar Imagen
            </button>
          )}
        </div>
      )}

      {/* Modal de imagen (versión mejorada) */}
      {isModalOpen && selectedImage && (
        <ImageModal item={selectedImage} onClose={closeImageModal} />
      )}
    </div>
  );
};

// Componente para mostrar una imagen individual
const ImageItem = ({ item, renderCase, onDelete, onSelect }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async (path) => {
      if (!path) return;

      setLoading(true);
      setImageError(false);

      try {
        const url = await fileService.getImage(path);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (err) {
        if (isMounted) {
          setImageError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Extraer información de la imagen de la respuesta
    if (item.respuesta) {
      let imageData;
      let imagePath = null;

      if (typeof item.respuesta === "string") {
        try {
          imageData = JSON.parse(item.respuesta);
          imagePath = imageData.valorimagen || imageData.path;
        } catch (e) {
          imagePath = item.respuesta;
        }
      } else if (
        typeof item.respuesta === "object" &&
        item.respuesta !== null
      ) {
        imageData = item.respuesta;
        imagePath = imageData.valorimagen || imageData.path;
      }

      if (imagePath) {
        fetchImageUrl(imagePath);
      } else {
        setImageError(true);
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [item.respuesta]);

  // Obtener la descripción de la imagen
  let description = "";
  if (typeof item.respuesta === "string") {
    try {
      const data = JSON.parse(item.respuesta);
      description = data.descripcion || "Sin descripción";
    } catch (e) {
      description = item.descripcion || "Sin descripción";
    }
  } else if (typeof item.respuesta === "object" && item.respuesta !== null) {
    description = item.respuesta.descripcion || "Sin descripción";
  }

  return (
    <div className="evidence-card">
      <div className="evidence-image-container" onClick={onSelect}>
        {loading ? (
          <div className="evidence-placeholder loading">
            <Spinner size="small" />
          </div>
        ) : imageError ? (
          <div className="evidence-placeholder error">
            <i className="fa fa-exclamation-triangle"></i>
            <span>Error al cargar</span>
          </div>
        ) : (
          <>
            <img src={imageUrl} alt={description} className="evidence-image" />
            <div className="evidence-overlay">
              <i className="fa fa-search-plus"></i>
            </div>
          </>
        )}
      </div>
      <div className="evidence-footer">
        <p className="evidence-description" title={description}>
          {description}
        </p>
        {renderCase === "create" && (
          <button
            className="evidence-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Eliminar esta imagen"
          >
            <i className="fa fa-trash"></i>
          </button>
        )}
      </div>
    </div>
  );
};

// Componente para el modal de visualización de imágenes
const ImageModal = ({ item, onClose }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async () => {
      let imagePath = null;

      if (typeof item.respuesta === "string") {
        try {
          const data = JSON.parse(item.respuesta);
          imagePath = data.valorimagen || data.path;
        } catch (e) {
          imagePath = item.respuesta;
        }
      } else if (
        typeof item.respuesta === "object" &&
        item.respuesta !== null
      ) {
        imagePath = item.respuesta.valorimagen || item.respuesta.path;
      }

      if (imagePath) {
        try {
          const url = await fileService.getImage(imagePath);
          if (isMounted) {
            setImageUrl(url);
            setLoading(false);
          }
        } catch (err) {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    };

    fetchImageUrl();

    return () => {
      isMounted = false;
    };
  }, [item]);

  // Extraer la descripción
  let description = "";
  if (typeof item.respuesta === "string") {
    try {
      const data = JSON.parse(item.respuesta);
      description = data.descripcion || "";
    } catch (e) {
      description = item.descripcion || "";
    }
  } else if (typeof item.respuesta === "object" && item.respuesta !== null) {
    description = item.respuesta.descripcion || "";
  }

  // Manejar el clic fuera del contenido modal para cerrar
  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("evidence-modal")) {
      onClose();
    }
  };

  return (
    <div className="evidence-modal" onClick={handleBackdropClick}>
      <div className="evidence-modal-content">
        <button className="evidence-modal-close" onClick={onClose}>
          <i className="fa fa-times"></i>
        </button>

        <div className="evidence-modal-body">
          {loading ? (
            <div className="evidence-modal-loading">
              <Spinner size="large" />
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={description}
              className="evidence-modal-image"
            />
          )}
        </div>

        {description && (
          <div className="evidence-modal-footer">
            <p className="evidence-modal-description">{description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagenesEvidencia;
